import { useId } from "react";

/**
 * QuestionMe "Dialogue Mark" logo — inline SVG + optional wordmark text.
 *
 * @param {Object}  props
 * @param {number}  [props.size=16]       Icon height in px
 * @param {boolean} [props.showText=true] Show "QuestionMe" wordmark beside the icon
 */
export function Logo({ size = 16, showText = true }) {
  // Unique gradient IDs per instance to avoid SVG collisions
  const uid = useId().replace(/:/g, "");

  const icon =
    size <= 32 ? (
      /* Simplified 32 px version */
      <svg
        viewBox="0 0 32 32"
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        style={{ display: "block", flexShrink: 0 }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={`qm-bg-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
          <linearGradient id={`qm-mk-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
        <rect width="32" height="32" rx="7" fill={`url(#qm-bg-${uid})`} />
        <rect x="4" y="5" width="16" height="13" rx="4" fill="rgba(255,255,255,0.28)" />
        <path d="M8,18 L11,18 L9,21" fill="rgba(255,255,255,0.28)" />
        <rect x="12" y="9" width="16" height="13" rx="4" fill="white" />
        <path d="M23,22 L20,22 L22,25" fill="white" />
        <text
          x="20"
          y="19.5"
          textAnchor="middle"
          fontFamily="system-ui,-apple-system,sans-serif"
          fontSize="11"
          fontWeight="800"
          fill={`url(#qm-mk-${uid})`}
        >
          ?
        </text>
      </svg>
    ) : (
      /* Full 512 px version for larger sizes */
      <svg
        viewBox="0 0 512 512"
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        style={{ display: "block", flexShrink: 0 }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={`qm-bg-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
          <linearGradient id={`qm-mk-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
        <rect width="512" height="512" rx="112" fill={`url(#qm-bg-${uid})`} />
        <rect x="72" y="88" width="250" height="190" rx="44" fill="rgba(255,255,255,0.25)" />
        <path d="M128,278 L168,278 L138,332" fill="rgba(255,255,255,0.25)" />
        <rect x="190" y="148" width="250" height="190" rx="44" fill="white" />
        <path d="M370,338 L330,338 L360,392" fill="white" />
        <text
          x="315"
          y="298"
          textAnchor="middle"
          fontFamily="system-ui,-apple-system,sans-serif"
          fontSize="150"
          fontWeight="800"
          fill={`url(#qm-mk-${uid})`}
        >
          ?
        </text>
      </svg>
    );

  if (!showText) return icon;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: size * 0.4 }}>
      {icon}
      <span
        style={{
          fontWeight: 700,
          fontSize: size,
          letterSpacing: "-0.02em",
          color: "inherit",
          lineHeight: 1,
        }}
      >
        QuestionMe
      </span>
    </span>
  );
}

export default Logo;
